import { useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowUp, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

interface FloatingArticleNavProps {
  className?: string;
}

/**
 * Floating action button component for article pages.
 * Apple-style frosted glass pill with scroll-to-top and back navigation.
 * Uses same show/hide scroll behavior as the TOC.
 */
export default function FloatingArticleNav({ className }: FloatingArticleNavProps) {
  const navigate = useNavigate();
  
  // Scroll-direction based visibility (matching TOC pattern)
  const [isVisible, setIsVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  
  const { scrollY } = useScroll();
  
  useMotionValueEvent(scrollY, "change", (latest) => {
    // Ignore scroll events during programmatic scrolling
    if (isProgrammaticScroll) return;
    
    const scrollDelta = latest - lastScrollY;
    
    // Hide when at the very top (no need for back-to-top there)
    if (latest <= 100) {
      setIsVisible(false);
    }
    // Hide when actively scrolling DOWN
    else if (scrollDelta > 10 && latest > 100) {
      setIsVisible(false);
    }
    // Show when scrolling up
    else if (scrollDelta < -30) {
      setIsVisible(true);
    }
    
    // Only update lastScrollY if we've moved significantly
    if (Math.abs(scrollDelta) > 5) {
      setLastScrollY(latest);
    }
  });
  
  // Scroll to top handler
  const handleScrollToTop = () => {
    setIsProgrammaticScroll(true);
    // ⚠️ ANALYTICS: Track scroll to top
    trackEvent(ANALYTICS_EVENTS.FAB_BUTTON_CLICKED, {
      button_type: 'scroll_to_top',
      current_scroll_position: window.scrollY,
      page: window.location.pathname,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // Re-enable scroll detection after animation
    setTimeout(() => {
      setIsProgrammaticScroll(false);
      setLastScrollY(window.scrollY);
    }, 800);
  };
  
  // Go back handler
  const handleGoBack = () => {
    // Check if user navigated here from within the site (same origin referrer)
    const referrer = document.referrer;
    const isSameOrigin = referrer && new URL(referrer).origin === window.location.origin;
    
    // ⚠️ ANALYTICS: Track go back
    trackEvent(ANALYTICS_EVENTS.FAB_BUTTON_CLICKED, {
      button_type: 'go_back',
      referrer: referrer,
      is_same_origin: isSameOrigin,
      destination: isSameOrigin && window.history.length > 1 ? 'previous_page' : '/learn',
      page: window.location.pathname,
    });
    
    if (isSameOrigin && window.history.length > 1) {
      navigate(-1);
    } else {
      // Default to Learn page if this is the first page or came from external
      navigate("/learn");
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ 
          opacity: isVisible ? 1 : 0, 
          y: isVisible ? 0 : 20,
          scale: isVisible ? 1 : 0.9,
          pointerEvents: isVisible ? "auto" : "none"
        }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className={cn(
          "fixed bottom-6 right-6 z-50",
          className
        )}
      >
        {/* Apple-style frosted glass pill */}
        <div
          className="flex items-center gap-0 rounded-full shadow-lg overflow-hidden"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "saturate(180%) blur(20px)",
            WebkitBackdropFilter: "saturate(180%) blur(20px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
          }}
        >
          {/* Back button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleGoBack}
                className="flex items-center justify-center w-12 h-12 text-coal hover:bg-grey/50 transition-colors active:scale-95"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" strokeWidth={2} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8}>
              <p>Go back</p>
            </TooltipContent>
          </Tooltip>
          
          {/* Divider */}
          <div className="w-px h-6 bg-coal/15" />
          
          {/* Scroll to top button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleScrollToTop}
                className="flex items-center justify-center w-12 h-12 text-coal hover:bg-grey/50 transition-colors active:scale-95"
                aria-label="Scroll to top"
              >
                <ArrowUp className="w-5 h-5" strokeWidth={2} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8}>
              <p>Back to top</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </motion.div>
    </TooltipProvider>
  );
}